import { useAuthStore } from '@/Stores/auth-v2-store';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { asFlag } from './useAssistance';

/**
 * L'offre d'assistance doit-elle être visible dans l'interface ?
 *
 * Règle unique, partagée par la liste des garanties, l'étape 3 de la création de
 * contrat et les vignettes : visible pour un INTERNAL, et pour un EXTERNAL dont la
 * société a l'option activée.
 *
 * `societyId` est la société AFFICHÉE — celle dont on regarde les garanties, ou
 * celle choisie pour le contrat — jamais la société de rattachement de
 * l'utilisateur. Un vendeur rattaché à la tête de groupe et à une agence doit voir
 * ce qui vaut pour l'agence qu'il a sélectionnée, pas pour sa société principale.
 * Même règle que le serveur depuis `fix(warranties): lire les garanties de la
 * société demandée par un user multi-société`.
 *
 * Aucune requête supplémentaire : les appelants ont déjà chargé cette
 * configuration, React Query dédoublonne sur la clé
 * `['society-configuration', 'effective', societyId]`.
 *
 * CE N'EST PAS UNE MESURE DE CONFIDENTIALITÉ. `assistanceFormulaId` est servi à
 * tous dans les listes ; on masque un réglage qui ne concerne pas le vendeur, on
 * ne protège pas un secret.
 */
export interface AssistanceVisibility {
  /** Faut-il afficher colonnes, filtres et mentions d'assistance ? */
  isVisible: boolean;
  /**
   * La réponse est-elle définitive ?
   *
   * `false` tant que la configuration d'un EXTERNAL est en vol. À distinguer de
   * « non visible » : décider trop tôt ferait apparaître une colonne après coup,
   * ou pire, effacerait un réglage. Les écrans qui ne font qu'afficher peuvent
   * l'ignorer ; ceux qui EFFACENT quelque chose doivent l'attendre.
   */
  isKnown: boolean;
}

export function useAssistanceVisibility(societyId: string | undefined): AssistanceVisibility {
  const { currentUser } = useAuthStore();
  const isInternal = currentUser?.type === 'INTERNAL';

  // Un INTERNAL voit l'offre sans condition : inutile d'interroger le serveur.
  const { data: config } = useEffectiveConfiguration(societyId, { enabled: !isInternal });

  if (isInternal) return { isVisible: true, isKnown: true };

  return {
    // `assistanceEnabled` arrive en tinyint (1/0) : on coerce, comme partout.
    isVisible: asFlag(config?.assistanceEnabled),
    isKnown: config !== undefined,
  };
}
